import { useEffect, useState } from 'react';
import LoadingSpinner from './LoadingSpinner';
import './ProgressOverlay.css';

const ProgressOverlay = ({ 
  isVisible, 
  progress, 
  onCancel = null 
}) => {
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState(null);

  useEffect(() => {
    let interval = null;
    if (isVisible) {
      setTimeElapsed(0);
      interval = setInterval(() => {
        setTimeElapsed(prev => prev + 1);
      }, 1000);
    } else {
      setTimeElapsed(0);
      setEstimatedTimeRemaining(null);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isVisible]);

  useEffect(() => {
    if (progress.total > 0 && progress.current > 0 && timeElapsed > 0) {
      const avgTimePerItem = timeElapsed / progress.current;
      const remainingItems = progress.total - progress.current;
      const estimated = Math.ceil(avgTimePerItem * remainingItems);
      setEstimatedTimeRemaining(estimated);
    } else if (progress.estimatedDuration && timeElapsed === 0) {
      // Use initial estimate if available
      setEstimatedTimeRemaining(progress.estimatedDuration);
    }
  }, [progress.current, progress.total, progress.estimatedDuration, timeElapsed]);

  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getProgressPercentage = () => {
    if (progress.total === 0) return 0;
    return Math.round((progress.current / progress.total) * 100);
  };

  if (!isVisible) return null;

  return (
    <div className="progress-overlay">
      <div className="progress-modal">
        <div className="progress-header">
          <LoadingSpinner size="medium" message="" />
          <h3>Logging your reading</h3>
        </div>

        <div className="progress-content">
          <div className="progress-message">
            {progress.message || 'Logging...'}
          </div>

          {progress.total > 0 && (
            <div className="progress-details">
              <div className="progress-bar-container">
                <div className="progress-bar">
                  <div 
                    className="progress-bar-fill" 
                    style={{ width: `${getProgressPercentage()}%` }}
                  />
                </div>
                <div className="progress-text">
                  {progress.current} of {progress.total} ({getProgressPercentage()}%)
                </div>
              </div>
            </div>
          )}

          <div className="progress-stats">
            <div className="stat-item">
              <span className="stat-label">Time elapsed</span>
              <span className="stat-value">{formatTime(timeElapsed)}</span>
            </div>
            {estimatedTimeRemaining && estimatedTimeRemaining > 0 && (
              <div className="stat-item">
                <span className="stat-label">Est. remaining</span>
                <span className="stat-value">{formatTime(estimatedTimeRemaining)}</span>
              </div>
            )}
          </div>

          <div className="progress-info">
            <p>
              <strong>Please don't close this window.</strong> 
              {progress.totalReports && (
                <> Creating {progress.totalReports} chapter logs and updating plans.</>
              )}
              {!progress.totalReports && (
                <> Large logs may take a few minutes as we save each chapter and update plans.</>
              )}
            </p>
          </div>

          {onCancel && (
            <div className="progress-actions">
              <button 
                className="cancel-button" 
                onClick={onCancel}
                type="button"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProgressOverlay;
